import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useModalDrag } from '../../../../hooks/useModalDrag';
import ModalHeader from '../../../../components/ui/ModalHeader';
import quoteDocumentsService from '../../../../services/quoteDocuments.service';
import type { QuoteDocumentRow } from '../../../../types/quoteDocuments';

interface Props {
  doc: QuoteDocumentRow;
  quoteNumber: number;
  revisionNumber: number;
  docTypes: string[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Edit an existing quote document's type + description (mirrors the Pro Details
 * DocsEditDialog). Sends only the changed fields via PATCH. Portaled to <body>
 * at z-[120] so it overlays the editor drawer.
 */
const QuoteDocEditDialog: React.FC<Props> = ({ doc, quoteNumber, revisionNumber, docTypes, onClose, onSaved }) => {
  const [type, setType] = useState(doc.documentType || '');
  const [description, setDescription] = useState(doc.description || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Rendered only while open (parent unmounts on close) — unmount resets the drag.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  // Offer the doc's current type even if it's not in the active option list.
  const typeOptions = docTypes.includes(type) || !type ? docTypes : [type, ...docTypes];

  const dirty = type !== (doc.documentType || '') || description !== (doc.description || '');

  const save = async () => {
    if (!dirty) { onClose(); return; }
    setBusy(true);
    setError(null);
    try {
      await quoteDocumentsService.update(quoteNumber, revisionNumber, doc.mapRecno, {
        documenttype: type !== (doc.documentType || '') ? type : undefined,
        description: description !== (doc.description || '') ? description : undefined,
      });
      onSaved();
      onClose();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err?.response?.data?.detail || err?.message || 'Failed to save changes.');
      setBusy(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-label="Edit document"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/40 p-6"
      onClick={busy ? undefined : onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="w-[420px] rounded-lg border border-slate-200 bg-panel shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title="Edit Document"
          onClose={busy ? undefined : onClose}
          dragHandleProps={dragHandleProps}
        />
        <div className="flex flex-col gap-3 px-4 py-4">
          <div className="text-[0.71875rem] text-slate-500 dark:text-slate-400 truncate" title={doc.filename}>{doc.filename}</div>
          <label className="flex flex-col gap-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Document Type
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              aria-label="Document type"
              className="h-7 rounded border border-slate-300 bg-white px-2 text-[0.78125rem] font-normal normal-case text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            >
              {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Description
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              aria-label="Document description"
              placeholder="Description (optional)"
              className="h-7 rounded border border-slate-300 bg-white px-2 text-[0.78125rem] font-normal normal-case text-slate-800 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            />
          </label>
          {error && <div role="alert" className="text-[0.75rem] text-red-600 dark:text-red-400">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-gray-700">
          <button type="button" onClick={onClose} disabled={busy} className="rounded border border-slate-300 bg-panel px-3 py-1.5 text-[0.75rem] text-slate-600 hover:bg-slate-50 dark:border-gray-600 dark:text-slate-300 dark:hover:bg-gray-700">Cancel</button>
          <button type="button" onClick={save} disabled={busy || !dirty} className="rounded border border-blue-700 bg-blue-600 px-3 py-1.5 text-[0.75rem] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default QuoteDocEditDialog;
